import React, { forwardRef, useMemo } from "react";
import "./reglamentoTemplate.css";
import { paginaActiva, buildCapRomanoMap } from "../../utils/reglamentoConfig";

import CoverPage from "./chapters/CoverPage";
import PreambuloPage from "./chapters/PreambuloPage";
import HabitantesPage from "./chapters/HabitantesPage";
import ProhibicionesPage from "./chapters/ProhibicionesPage";
import PersonalComisionPage from "./chapters/PersonalComisionPage";
import AdministradorPage from "./chapters/AdministradorPage";
import AccesosMascotasPage from "./chapters/AccesosMascotasPage";
import AscensoresGarajesPage from "./chapters/AscensoresGarajesPage";
import GimnasioJardinesPage from "./chapters/GimnasioJardinesPage";
import BarbacoaPage from "./chapters/BarbacoaPage";
import PiscinaPage from "./chapters/PiscinaPage";
import PagosVaciosPage from "./chapters/PagosVaciosPage";
import SancionesPage from "./chapters/SancionesPage";
import ClosingPage from "./chapters/ClosingPage";

// Documento Reglamento Interno: assembler de las paginas activas.
// Cada pagina se renderiza solo si al menos uno de sus capitulos esta visible.
// Numeracion X/Y dinamica + indice TOC contiguo se calculan en cada render.

// Orden canonico de paginas (cover y preambulo siempre; el resto segun paginaActiva).
function buildPagesList(config) {
  const cv = config.capitulosVisibles || {};
  const list = [
    { key: "cover", Componente: CoverPage },
    { key: "preambulo", Componente: PreambuloPage },
  ];
  if (paginaActiva("habitantes", cv))         list.push({ key: "habitantes",         Componente: HabitantesPage });
  if (paginaActiva("prohibiciones", cv))      list.push({ key: "prohibiciones",      Componente: ProhibicionesPage });
  if (paginaActiva("personal-comision", cv))  list.push({ key: "personal-comision",  Componente: PersonalComisionPage });
  if (paginaActiva("administrador", cv))      list.push({ key: "administrador",      Componente: AdministradorPage });
  if (paginaActiva("accesos-mascotas", cv))   list.push({ key: "accesos-mascotas",   Componente: AccesosMascotasPage });
  if (paginaActiva("ascensores-garajes", cv)) list.push({ key: "ascensores-garajes", Componente: AscensoresGarajesPage });
  if (paginaActiva("gimnasio-jardines", cv))  list.push({ key: "gimnasio-jardines",  Componente: GimnasioJardinesPage });
  if (paginaActiva("barbacoa", cv))           list.push({ key: "barbacoa",           Componente: BarbacoaPage });
  if (paginaActiva("piscina", cv))            list.push({ key: "piscina",            Componente: PiscinaPage });
  if (paginaActiva("pagos-vacios", cv))       list.push({ key: "pagos-vacios",       Componente: PagosVaciosPage });
  if (paginaActiva("sanciones", cv))          list.push({ key: "sanciones",          Componente: SancionesPage });
  list.push({ key: "closing", Componente: ClosingPage });
  return list;
}

// TOC dinamico: solo capitulos visibles, numero romano SECUENCIAL desde el mapa,
// y pagina donde aparece cada uno.
function buildToc(config, paginas, capRomanoMap) {
  const cv = config.capitulosVisibles || {};
  const pageOf = (key) => {
    const i = paginas.findIndex((p) => p.key === key);
    return i >= 0 ? i + 1 : 0;
  };
  // Mapeo cap.key -> { titulo, paginaKey } para construir el TOC en orden.
  const meta = [
    ["derechos",            "Derechos de los habitantes",          "habitantes"],
    ["obligaciones",        "Obligaciones de los habitantes",      "habitantes"],
    ["prohibiciones",       "Prohibiciones",                        "prohibiciones"],
    ["personal",            "Personal del edificio",                "personal-comision"],
    ["comision",            "Comisión del edificio",                "personal-comision"],
    ["administrador",       "Administrador",                        "administrador"],
    ["personalContratado",  "Personal de servicio contratado",      "accesos-mascotas"],
    ["hallAccesos",         "Hall de entrada y accesos",            "accesos-mascotas"],
    ["mascotas",            "Animales domésticos",                  "accesos-mascotas"],
    ["ascensores",          "Ascensores",                           "ascensores-garajes"],
    ["garajes",             "Garajes",                              "ascensores-garajes"],
    ["gimnasio",            "Gimnasio",                             "gimnasio-jardines"],
    ["jardines",            "Jardines y exteriores",                "gimnasio-jardines"],
    ["barbacoa",            "Barbacoa",                             "barbacoa"],
    ["piscina",             "Piscina, hidromasaje y sauna",         "piscina"],
    ["pagos",               "Pago de gastos comunes",               "pagos-vacios"],
    ["vacios",              "Apartamentos desocupados",             "pagos-vacios"],
    ["sanciones",           "Sanciones",                            "sanciones"],
    ["vigencia",            "Vigencia y disposiciones",             "sanciones"],
  ];
  return meta
    .filter(([key]) => cv[key])
    .map(([key, titulo, paginaKey]) => ({
      romano: capRomanoMap[key],
      titulo,
      pag: pageOf(paginaKey),
    }));
}

const ReglamentoTemplate = forwardRef(function ReglamentoTemplate({ config }, ref) {
  const paginas = useMemo(() => buildPagesList(config), [config]);
  const capRomanoMap = useMemo(() => buildCapRomanoMap(config.capitulosVisibles || {}), [config.capitulosVisibles]);
  const tocItems = useMemo(() => buildToc(config, paginas, capRomanoMap), [config, paginas, capRomanoMap]);
  const total = paginas.length;

  return (
    <div className="habitia-reglamento" ref={ref}>
      {paginas.map((p, idx) => {
        const Componente = p.Componente;
        const pagNum = idx + 1;
        const extraProps = p.key === "preambulo" ? { tocItems } : {};
        return (
          <Componente
            key={p.key}
            config={config}
            pagNum={pagNum}
            pagTotal={total}
            capRomanoMap={capRomanoMap}
            {...extraProps}
          />
        );
      })}
    </div>
  );
});

export default ReglamentoTemplate;
